const APPLICATION_LANGUAGE = 'ru';

const isPageTranslated = () => {
  const documentLanguage = document.documentElement.lang
    .toLowerCase()
    .split('-')[0];
  const translateClasses = ['translated-ltr', 'translated-rtl'];
  const hasTranslateClass = translateClasses.some(
    (className) =>
      document.documentElement.classList.contains(className) ||
      document.body.classList.contains(className),
  );
  const hasTranslateCookie = document.cookie
    .split(';')
    .some((cookie) => cookie.trim().startsWith('googtrans='));

  return (
    documentLanguage !== APPLICATION_LANGUAGE ||
    hasTranslateClass ||
    hasTranslateCookie
  );
};

export const bindTranslatedInputFix = (container: HTMLElement) => {
  let animationFrameId: number | undefined;
  let hasTranslatedPage = false;
  const hiddenCaretInputs = new Set<HTMLInputElement>();
  const nativeValueSetter = Object.getOwnPropertyDescriptor(
    HTMLInputElement.prototype,
    'value',
  )?.set;

  const detectTranslation = () => {
    if (isPageTranslated()) hasTranslatedPage = true;
  };

  const prepareTranslatedInput = (event: Event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return null;

    detectTranslation();

    if (hasTranslatedPage) {
      input.style.caretColor = 'transparent';
      hiddenCaretInputs.add(input);
      return input;
    }

    return null;
  };

  const updateInputFromEnd = (
    input: HTMLInputElement,
    replacement: string,
    inputType: string,
  ) => {
    const moveCaretToEnd = () => {
      const cursor = input.value.length;
      input.setSelectionRange(cursor, cursor);
    };

    moveCaretToEnd();
    const isDeletion = inputType.startsWith('delete');
    let nextValue: string;
    let data = replacement.replace(/\D/g, '');

    if (isDeletion) {
      let digitPosition = input.value.length - 1;

      while (digitPosition >= 0 && !/[0-9]/.test(input.value[digitPosition])) {
        digitPosition -= 1;
      }

      if (digitPosition < 0) return false;

      nextValue =
        input.value.slice(0, digitPosition) +
        input.value.slice(digitPosition + 1);
      data = '';
    } else {
      if (!data) return false;
      nextValue = input.value + data;
    }

    if (!nativeValueSetter) return false;

    nativeValueSetter.call(input, nextValue);
    input.dispatchEvent(
      new InputEvent('input', {
        bubbles: true,
        data: data || null,
        inputType,
      }),
    );

    if (animationFrameId !== undefined) cancelAnimationFrame(animationFrameId);
    animationFrameId = requestAnimationFrame(() => {
      moveCaretToEnd();
      animationFrameId = undefined;
    });

    return true;
  };

  const handleBeforeInput = (event: Event) => {
    const inputEvent = event as InputEvent;
    const input = prepareTranslatedInput(event);

    if (!input || inputEvent.isComposing || !event.cancelable) {
      return;
    }

    let replacement = '';
    if (inputEvent.inputType.startsWith('insert')) {
      replacement =
        inputEvent.data ?? inputEvent.dataTransfer?.getData('text/plain') ?? '';
      if (!replacement) return;
    } else if (
      inputEvent.inputType === 'deleteContentBackward' ||
      inputEvent.inputType === 'deleteContentForward'
    ) {
      if (input.value.length === 0) return;
    } else {
      return;
    }

    if (updateInputFromEnd(input, replacement, inputEvent.inputType)) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    const input = prepareTranslatedInput(event);
    const isDeletion = event.key === 'Backspace' || event.key === 'Delete';

    if (!input || event.isComposing || !event.cancelable) {
      return;
    }

    input.setSelectionRange(input.value.length, input.value.length);

    if (
      isDeletion &&
      updateInputFromEnd(
        input,
        '',
        event.key === 'Backspace'
          ? 'deleteContentBackward'
          : 'deleteContentForward',
      )
    ) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  container.addEventListener('beforeinput', handleBeforeInput, true);
  container.addEventListener('keydown', handleKeyDown, true);

  return () => {
    if (animationFrameId !== undefined) cancelAnimationFrame(animationFrameId);
    hiddenCaretInputs.forEach((input) => {
      input.style.caretColor = '';
    });
    container.removeEventListener('beforeinput', handleBeforeInput, true);
    container.removeEventListener('keydown', handleKeyDown, true);
  };
};

export const bindTranslatedMaskedNumericInputFix = (
  input: HTMLInputElement,
) => {
  let hasTranslatedPage = false;
  const nativeValueSetter = Object.getOwnPropertyDescriptor(
    HTMLInputElement.prototype,
    'value',
  )?.set;

  const prepareInput = () => {
    if (!hasTranslatedPage && isPageTranslated()) hasTranslatedPage = true;
    if (!hasTranslatedPage) return false;

    input.style.caretColor = 'transparent';
    return true;
  };

  const isEmptyMask = (value: string) => !/[0-9]/.test(value);

  const getInsertPosition = (value: string) => {
    if (isEmptyMask(value)) return 0;

    const position = value.indexOf('_');
    return position === -1 ? value.length : position;
  };

  const getLastSymbolPosition = (value: string) => {
    let position = getInsertPosition(value) - 1;

    while (position >= 0 && !/[0-9]/.test(value[position])) {
      position -= 1;
    }

    return position;
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (!prepareInput()) return;

    const isDeletion = event.key === 'Backspace' || event.key === 'Delete';
    const position = isDeletion
      ? getLastSymbolPosition(input.value) + 1
      : getInsertPosition(input.value);

    input.setSelectionRange(position, position);
  };

  const handleBeforeInput = (event: InputEvent) => {
    if (!prepareInput() || !nativeValueSetter) return;

    const isInsertion = event.inputType.startsWith('insert');
    const isDeletion = event.inputType.startsWith('delete');

    if (!isInsertion && !isDeletion) return;

    const previousValue = input.value;
    let nextValue: string;
    let position: number;

    if (isInsertion) {
      const insertedValue = (
        event.data ??
        event.dataTransfer?.getData('text/plain') ??
        ''
      ).replace(/\D/g, '');

      if (!insertedValue) return;

      const sourceValue = isEmptyMask(previousValue) ? '' : previousValue;
      position = getInsertPosition(sourceValue);
      nextValue =
        sourceValue.slice(0, position) +
        insertedValue +
        sourceValue.slice(position);
      position += insertedValue.length;
    } else {
      const symbolPosition = getLastSymbolPosition(previousValue);
      if (symbolPosition < 0) return;

      nextValue =
        previousValue.slice(0, symbolPosition) +
        previousValue.slice(symbolPosition + 1);
      position = symbolPosition;
    }

    event.preventDefault();
    event.stopPropagation();
    nativeValueSetter.call(input, nextValue);
    input.setSelectionRange(position, position);
    input.dispatchEvent(
      new InputEvent('input', {
        bubbles: true,
        data: isInsertion ? event.data : null,
        inputType: event.inputType,
      }),
    );
  };

  input.addEventListener('keydown', handleKeyDown, true);
  input.addEventListener('beforeinput', handleBeforeInput, true);

  return () => {
    input.style.caretColor = '';
    input.removeEventListener('keydown', handleKeyDown, true);
    input.removeEventListener('beforeinput', handleBeforeInput, true);
  };
};
